import { useEffect, useState } from "react";
import type { ReportGroup, ReportQuestionCandidate } from "../../../hooks";
import {
  formatReportSettingJson,
  parseReportSettingJson,
  REPORT_SETTING_JSON_SAMPLE,
} from "../reportSetting";

interface ReportGroupDialogProps {
  open: boolean;
  /** 編集対象。null なら新規追加 */
  reportGroup: ReportGroup | null;
  /** このタームで実際に使われている設問。question_id の書き写しを間違えないために出す */
  questionCandidates: readonly ReportQuestionCandidate[];
  onClose: () => void;
  /** 保存する。失敗した理由を返し、成功したら null を返す */
  onSubmit: (form: ReportGroupForm) => Promise<string | null>;
}

export interface ReportGroupForm {
  name: string;
  reportSettingJson: Record<string, unknown>;
}

export function ReportGroupDialog({
  open,
  reportGroup,
  questionCandidates,
  onClose,
  onSubmit,
}: ReportGroupDialogProps) {
  const [name, setName] = useState("");
  const [reportSettingJsonText, setReportSettingJsonText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // 画面上部のエラーはダイアログの裏に隠れるため、保存の失敗はここにも出す
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ダイアログは常時マウントされるため、開くたびに編集対象から再シードする
  useEffect(() => {
    if (!open) return;
    setName(reportGroup?.name ?? "");
    setReportSettingJsonText(
      reportGroup
        ? formatReportSettingJson(reportGroup.reportSettingJson)
        : REPORT_SETTING_JSON_SAMPLE,
    );
    setSubmitError(null);
  }, [open, reportGroup]);

  if (!open) return null;

  const parsed = parseReportSettingJson(reportSettingJsonText);
  // 入力途中でエラーを出し続けないよう、何か書かれてからだけ知らせる
  const jsonError =
    reportSettingJsonText.trim().length > 0 ? parsed.error : null;
  const canSubmit =
    name.trim().length > 0 && parsed.value !== null && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || parsed.value === null) return;
    setSubmitting(true);
    setSubmitError(null);
    const errorMessage = await onSubmit({
      name: name.trim(),
      reportSettingJson: parsed.value,
    });
    setSubmitting(false);
    if (errorMessage === null) {
      onClose();
      return;
    }
    setSubmitError(errorMessage);
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-[90%] max-w-[640px] overflow-auto rounded-xl bg-white p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-1 font-semibold text-[var(--color-text-primary)] text-lg">
          {reportGroup ? "レポート設定を編集" : "レポート設定を追加"}
        </h3>
        <p className="mt-0 mb-5 text-[13px] text-[var(--color-text-secondary)]">
          取り込んだ回答をまとめて、受け取る人ごとのレポートを作ります
        </p>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="reportGroupName"
              className="mb-1.5 block font-medium text-[var(--color-text-primary)] text-sm"
            >
              レポート名
            </label>
            <input
              id="reportGroupName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例: 202608_〇〇様_事前上司インタビュー"
              required
              className="box-border w-full rounded-md border border-[var(--color-border-default)] px-3 py-2.5 text-sm outline-none"
            />
            <p className="mt-1 mb-0 text-[13px] text-[var(--color-text-secondary)]">
              クライアントとコーチのレポート一覧に、この名前がそのまま表示されます
            </p>
          </div>
          <div className="mb-5">
            <label
              htmlFor="reportSettingJson"
              className="mb-1.5 block font-medium text-[var(--color-text-primary)] text-sm"
            >
              設問構成
            </label>
            <textarea
              id="reportSettingJson"
              value={reportSettingJsonText}
              onChange={(e) => setReportSettingJsonText(e.target.value)}
              rows={16}
              spellCheck={false}
              className={`box-border w-full rounded-md border px-3 py-2.5 font-mono text-[13px] outline-none ${
                jsonError
                  ? "border-[#ef4444]"
                  : "border-[var(--color-border-default)]"
              }`}
            />
            {jsonError ? (
              <p className="mt-1 mb-0 text-[#ef4444] text-[13px]">
                {jsonError}
              </p>
            ) : (
              <p className="mt-1 mb-0 text-[13px] text-[var(--color-text-secondary)]">
                レポートに出す設問を節ごとに並べます
              </p>
            )}
          </div>
          <div className="mb-5">
            <span className="mb-1.5 block font-medium text-[var(--color-text-primary)] text-sm">
              このタームで使われている設問
            </span>
            {questionCandidates.length === 0 ? (
              <p className="mt-0 mb-0 text-[13px] text-[var(--color-text-secondary)]">
                取り込み済みの回答がまだありません。回答を取り込むと、ここに
                question_id の一覧が出ます
              </p>
            ) : (
              <>
                <p className="mt-0 mb-2 text-[13px] text-[var(--color-text-secondary)]">
                  question_id
                  はここにある値と一致させてください。合っていない設問はレポートに出ません
                </p>
                <ul className="m-0 max-h-[160px] list-none overflow-auto rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-subtle-gray)] p-3">
                  {questionCandidates.map((candidate) => (
                    <li key={candidate.questionId} className="mb-1 last:mb-0">
                      <code className="font-mono text-[13px] text-[var(--color-text-primary)]">
                        {candidate.questionId}
                      </code>
                      <span className="ml-2 text-[13px] text-[var(--color-text-secondary)]">
                        {candidate.name}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
          {submitError && (
            <p
              role="alert"
              className="mt-0 mb-4 rounded-md bg-[#fef2f2] px-3 py-2.5 text-[#b91c1c] text-[13px]"
            >
              {submitError}
            </p>
          )}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className={`rounded-md border border-[var(--color-text-secondary)] bg-white px-4 py-2.5 font-medium text-[var(--color-text-secondary)] text-sm ${submitting ? "cursor-not-allowed" : "cursor-pointer"}`}
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className={`rounded-md border-none px-4 py-2.5 font-medium text-sm text-white ${
                canSubmit
                  ? "cursor-pointer bg-[var(--color-primary-500)]"
                  : "cursor-not-allowed bg-[#9ca3af]"
              }`}
            >
              {submitting ? "保存中..." : reportGroup ? "更新" : "追加"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
